// combatpro/encounter/encounterStoreEngine.js
//
// Combat PRO - Encounter Store Engine (Stage 115: pure serialization + store shape).
//
// Pure, UI-independent migration of the legacy vi* encounter STORE core
// (battletracker.html 10358-10555). Data in, data out: it captures a combat
// state into a plain serializable object, applies a captured object back into a
// partial state, builds the default/empty store shapes, normalizes/upgrades a
// loaded store, computes the autosave signature, derives per-encounter metrics,
// and does the tier slot math. It owns NO state and performs NO side effects.
//
// FORBIDDEN here and absent (deferred to the Persistence Runtime / Dashboard
// stages, per the Stage 114 audit): localStorage / sessionStorage, saveToCloud /
// loadFromCloud, saveState, autosave timers (setInterval / setTimeout), dirty
// tracking, the status strip, dashboard CRUD / switching / rendering, and the
// tier MEDIUM read (viGetTier's window.VI_TIER || localStorage.getItem). React,
// DOM, window, document, fetch, and CombatContext ownership are all absent. No
// optional chaining. No nullish coalescing. No em dash. No literal em dash in
// source (none needed here).
//
// Source mapping (battletracker.html):
//   - cloneDeep(x)                 viClone (10358): JSON round-trip deep clone,
//                                  guarded (returns the input on failure).
//   - captureCombat(state)         viCaptureCombat (10389): snapshot the combat
//                                  fields into a deep-cloned plain object.
//   - applyCombat(combat)          viApplyCombat (10406) FIELD WRITES ONLY: returns
//                                  a partial-state object of the same fields, guarded
//                                  exactly as legacy (Array.isArray / typeof checks).
//                                  The ~14 imperative DOM-refresh calls (10418-10429)
//                                  are an APPROVED Category-E omission - React
//                                  re-renders declaratively from the applied state.
//   - defaultCombat(pristine)      viDefaultCombat (10402): pristine clone when
//                                  provided, else the empty combat. The legacy
//                                  VI_PRISTINE_COMBAT global read is an injected
//                                  parameter here (no global reads).
//   - defaultMeta(i)               viDefaultMeta (10385): verbatim meta defaults.
//   - emptyContainers()            viEmptyContainers (10382): verbatim container
//                                  shape.
//   - newEncounterData(i,pristine) viNewEncounterData (10433): meta + combat +
//                                  containers.
//   - normalizeStore(store,pristine) viNormalizeStore (10440): 12-slot upgrade /
//                                  defaulting, Draft->Design migration, status /
//                                  theme / tags guards, container merge, activeIndex
//                                  clamp.
//   - buildSignature(state)        viSig (10516): the autosave change fingerprint
//                                  (NOT the save payload) - a stable JSON string.
//   - monsterCount / hpPercent / aliveCount   viMonsterCount (10487) / viHpPct
//                                  (10489) / viAliveCount (10495) via viPopMonsters
//                                  (10483). aliveCount takes a combat object here
//                                  (the legacy read the live `monsters` global).
//   - unlockedSlots(tier) / slotLocked(i,tier)   viUnlockedSlots (10479) /
//                                  viSlotLocked (10480): pure tier math. viGetTier
//                                  (the medium read) is NOT ported - deferred.
//
// FIELD MAPPING (faithful, documented, not a behaviour change):
//   - legacy `viPlayers` (window.viPlayers)  -> React `players` (CombatContext
//     state). Same data, injected instead of global.
//   - legacy `vi_lastActionsStore` (a separate global object) is NOT a separate
//     field here: in the React migration last actions live on `monster.lastActions`
//     (already inside `monsters`), so they are captured/applied inside `monsters`.
//     The legacy separate field is therefore subsumed, not dropped.

// Fixed slot count and the valid status / theme sets (battletracker.html
// 10332-10346), ported verbatim so normalizeStore's guards match legacy exactly.
import { defaultBrief, normalizeBrief } from './encounterBriefEngine';
import { defaultDmOps, normalizeDmOps } from '../runtime/dmOpsEngine';
import { normalizeMonster } from '../monsters/monsterEngine';

export const ENC_COUNT = 12;
export const ENC_STATUSES = ['Empty', 'Design', 'Ready', 'In Progress', 'Completed', 'Archived'];
export const THEMES = {
  'default': '', 'crimson': '#DC2626', 'emerald': '#10B981', 'azure': '#3B82F6',
  'gold': '#C9950C', 'violet': '#8B5CF6', 'slate': '#64748B'
};

// Deep clone via JSON round-trip. Verbatim viClone (10358): on failure returns the
// input unchanged (the legacy swallowed the error and returned x).
export function cloneDeep(x) {
  try {
    return JSON.parse(JSON.stringify(x));
  } catch (e) {
    return x;
  }
}

// Capture the live combat fields into a deep-cloned plain object. Mirrors
// viCaptureCombat (10389). Reads the injected state (no globals). Legacy viPlayers
// maps to state.players; legacy vi_lastActionsStore is subsumed by monster
// .lastActions inside monsters (see FIELD MAPPING above).
export function captureCombat(state) {
  const s = state || {};
  return cloneDeep({
    monsters: s.monsters,
    currentRound: s.currentRound,
    activeMonsterCount: s.activeMonsterCount,
    currentCombatantIndex: s.currentCombatantIndex,
    rollHistory: s.rollHistory,
    combatLogEntries: s.combatLogEntries,
    historyStack: s.historyStack,
    players: (s.players || []),
    treasureLog: (s.treasureLog || []),
    templates: (s.templates || [])
  });
}

// Build a partial-state object from a captured combat object, guarded exactly as
// viApplyCombat (10406-10417). Returns ONLY the fields present/valid on the input,
// so the caller can spread it over previous state. NO DOM, NO side effects: the
// legacy imperative refresh block (10418-10429) is the approved Category-E omission.
export function applyCombat(combat) {
  const out = {};
  if (!combat) { return out; }
  const c = cloneDeep(combat);
  // Every monster is brought onto the live runtime shape (normalizeMonster) so a
  // stored or externally imported record missing fields (e.g. the generator
  // import's name-only stubs) can never reach a page that reads them directly.
  // Existing values are preserved; only missing / wrong-typed fields are filled.
  if (Array.isArray(c.monsters)) { out.monsters = c.monsters.map(normalizeMonster); }
  if (Array.isArray(c.treasureLog)) { out.treasureLog = c.treasureLog; }
  if (Array.isArray(c.templates)) { out.templates = c.templates; }
  if (typeof c.currentRound !== 'undefined') { out.currentRound = c.currentRound; }
  if (typeof c.activeMonsterCount !== 'undefined') { out.activeMonsterCount = c.activeMonsterCount; }
  if (typeof c.currentCombatantIndex !== 'undefined') { out.currentCombatantIndex = c.currentCombatantIndex; }
  if (Array.isArray(c.rollHistory)) { out.rollHistory = c.rollHistory; }
  if (Array.isArray(c.combatLogEntries)) { out.combatLogEntries = c.combatLogEntries; }
  if (Array.isArray(c.historyStack)) { out.historyStack = c.historyStack; }
  if (Array.isArray(c.players)) { out.players = c.players; }
  return out;
}

// Compose the full cloud persistence payload (Stage 125A: Encounter Store Cloud
// Persistence). This is the legacy saveToCloud payload shape, verbatim
// (battletracker.html 3891-3915): the active-combat fields at the top level PLUS
// the complete 12-slot store under btEncounterStore, with the ACTIVE slot's
// combat replaced by the live capture and its lastModified stamped - the effect
// of the legacy viSyncActiveFromLive (10462) expressed as a PURE fold (no state
// mutation; the state-writing sync remains owned by CombatContext's
// syncActiveFromLive). The legacy field name btEncounterStore is kept verbatim,
// and the top-level active-combat superset doubles as backward compatibility:
// a consumer that ignores btEncounterStore reads a valid Stage 118 payload.
// btMonsterTemplates is intentionally NOT attached (approved D1): the React
// template runtime (Stage 63) owns template cloud persistence as per-record CRUD
// on /user/monsters, so attaching templates here would create a second
// persistence path. Reuses captureCombat / cloneDeep; when no store exists on
// the state, the bare active capture is returned (graceful degradation).
export function capturePersistPayload(state) {
  const s = state || {};
  const active = captureCombat(s);
  const store = s.encounterStore;
  if (!store || !Array.isArray(store.encounters)) {
    return active;
  }
  const activeFam = activeFamilyOf(store);
  const activeIdx = store.activeIndex;
  const migrated = (store.savedMigrated === true);

  // The DURABLE account-backed document must contain ONLY encounters permitted as
  // persistent saves under the user's entitlement (correction: final persistence
  // authority). Rule per family:
  //   - a SAVED slot (meta.saved + populated) is kept as a durable bank entry;
  //   - the ACTIVE slot keeps its live combat as an explicitly TRANSIENT record so
  //     the current fight survives a reload - it is flagged transient and, being
  //     unsaved, is not a reusable saved bank entry;
  //   - every other UNSAVED slot is emptied (temporary runtime state, never durable).
  // The live in-memory store keeps all temporary slots for normal play; only the
  // PERSISTED payload is stripped. Before the one-time migration resolves
  // (savedMigrated !== true) NOTHING is stripped, so existing data is never silently
  // lost - the migration UI brings it under the save authority first.
  function foldFamily(fam) {
    var key = familyArrayKey(fam);
    var arr = Array.isArray(store[key]) ? store[key] : [];
    var out = [];
    for (var i = 0; i < ENC_COUNT; i++) {
      var slot = arr[i];
      var isActive = (fam === activeFam && i === activeIdx);
      var folded = slot;
      if (isActive && slot) {
        folded = Object.assign({}, slot, {
          combat: cloneDeep(active),
          meta: Object.assign({}, slot.meta, { lastModified: Date.now() })
        });
      }
      if (!migrated) {
        out.push(folded || newFamilySlot(fam, i, null));
        continue;
      }
      if (slotIsPrepared(folded)) {
        out.push(folded);
      } else if (isActive && slotHasContent(folded)) {
        out.push(Object.assign({}, folded, { meta: Object.assign({}, folded.meta, { saved: false, transient: true }) }));
      } else {
        out.push(newFamilySlot(fam, i, null));
      }
    }
    return out;
  }

  var newStore = Object.assign({}, store, {
    encounters: foldFamily('standard'),
    randomEncounters: foldFamily('random'),
    waveEncounters: foldFamily('wave')
  });
  return Object.assign({}, active, { btEncounterStore: cloneDeep(newStore) });
}

// Family-agnostic empty-slot factory (used by the durable-strip fold above).
function newFamilySlot(fam, i, pristine) {
  if (fam === 'random') { return newRandomEncounter(i, pristine); }
  if (fam === 'wave') { return newWaveEncounter(i, pristine); }
  return newEncounterData(i, pristine);
}

// Empty combat, or a clone of the injected pristine combat. Mirrors
// viDefaultCombat (10402); the VI_PRISTINE_COMBAT global is an injected parameter.
export function defaultCombat(pristine) {
  if (pristine) { return cloneDeep(pristine); }
  return {
    monsters: [], currentRound: 1, activeMonsterCount: 16, currentCombatantIndex: 0,
    rollHistory: [], combatLogEntries: [], historyStack: [], players: [], treasureLog: [], templates: []
  };
}

// Default encounter metadata. Verbatim viDefaultMeta (10385).
export function defaultMeta(i) {
  return {
    name: 'Encounter ' + (i + 1), status: 'Empty', location: '', difficulty: '',
    campaign: '', partyLevel: '', description: '', notes: '', colorTheme: 'default',
    tags: [], lastModified: Date.now()
  };
}

// Default container shape. Verbatim viEmptyContainers (10382).
export function emptyContainers() {
  return {
    maps: [], npcs: [], treasure: [], rewards: [], environment: {}, hazards: [],
    objectives: [], location: '', sessionNotes: '', campaignNotes: ''
  };
}

// A fresh encounter slot { meta, combat, containers }. Mirrors viNewEncounterData
// (10433). pristine is forwarded to defaultCombat.
export function newEncounterData(i, pristine) {
  return { meta: defaultMeta(i), combat: defaultCombat(pristine), containers: emptyContainers(), encounterBrief: defaultBrief(), dmOps: defaultDmOps() };
}

// ---- Encounter FAMILIES (Random & Wave expansion) --------------------------
// Combat PRO has three encounter families. STANDARD keeps the existing 12-slot
// `encounters` array UNCHANGED (internal numbers 1-12, entitlement bound intact).
// RANDOM and WAVE are two NEW bounded 12-slot banks (siblings on the same store,
// internal 13-24 / 25-36), each a generated encounter slot that reuses the exact
// slot shape { meta, combat, containers, encounterBrief, dmOps } plus a `gen`
// object, so once generated they flow through the identical combat runtime. This
// keeps the Standard records and their tier/slot math untouched (spec 2, 24).
export var FAMILIES = ['standard', 'random', 'wave'];
export var FAMILY_LABELS = { standard: 'Standard', random: 'Random', wave: 'Wave' };

// The store array key that holds a family's slots.
export function familyArrayKey(family) {
  if (family === 'random') { return 'randomEncounters'; }
  if (family === 'wave') { return 'waveEncounters'; }
  return 'encounters';
}
export function familyLabel(family) {
  return FAMILY_LABELS.hasOwnProperty(family) ? FAMILY_LABELS[family] : 'Standard';
}
// Stable internal absolute number for logging/identity (Standard 1-12, Random
// 13-24, Wave 25-36); localIdx is 0-based (spec 2). User-facing selection uses the
// family-local number (localIdx + 1) + the custom name, never this absolute number.
export function internalNumber(family, localIdx) {
  var base = (family === 'random') ? 12 : (family === 'wave') ? 24 : 0;
  return base + (parseInt(localIdx, 10) || 0) + 1;
}
// The slots array of a store's ACTIVE family, and the active slot itself.
export function familySlots(store, family) {
  if (!store) { return []; }
  var arr = store[familyArrayKey(family)];
  return Array.isArray(arr) ? arr : [];
}
export function activeFamilyOf(store) {
  return (store && FAMILIES.indexOf(store.activeFamily) !== -1) ? store.activeFamily : 'standard';
}
export function activeSlotOf(store) {
  if (!store) { return null; }
  var arr = familySlots(store, activeFamilyOf(store));
  var i = (typeof store.activeIndex === 'number') ? store.activeIndex : 0;
  return arr[i] || null;
}

// Generated-slot lifecycle states (spec 11, 18). Distinct from the Standard status
// vocabulary so it never disturbs Standard/dashboard/entitlement semantics.
export var GEN_STATES = ['empty', 'generated', 'ready', 'active', 'used'];

// Default generator selections for a Random slot (spec 8).
export function defaultRandomGen() {
  return {
    family: 'random', state: 'empty',
    level: 5, useParty: false, difficulty: 'medium', count: 4,
    creatureType: 'any', environment: 'any', source: 'all', composition: 'random',
    summary: '', headline: '', band: '', signature: '', generatedAt: 0
  };
}
// Default generator selections for a Wave slot (spec 13).
export function defaultWaveGen() {
  return {
    family: 'wave', state: 'empty',
    level: 5, useParty: false, difficulty: 'medium', totalMonsters: 8,
    creatureType: 'any', environment: 'any', source: 'all', waves: '3', style: 'standard',
    summary: '', headline: '', band: '', waveSummary: [], waveCount: 0, signature: '', generatedAt: 0
  };
}
// Merge a persisted gen object onto its family defaults, guarding the state field.
export function normalizeGen(gen, family) {
  var base = (family === 'wave') ? defaultWaveGen() : defaultRandomGen();
  var g = (gen && typeof gen === 'object') ? gen : {};
  var out = Object.assign({}, base, g);
  out.family = (family === 'wave') ? 'wave' : 'random';
  if (GEN_STATES.indexOf(out.state) === -1) { out.state = 'empty'; }
  if (family === 'wave' && !Array.isArray(out.waveSummary)) { out.waveSummary = []; }
  return out;
}
// A fresh Random slot (spec 2: default name "Random Encounter N").
export function newRandomEncounter(i, pristine) {
  return {
    meta: Object.assign({}, defaultMeta(i), { name: 'Random Encounter ' + (i + 1) }),
    combat: defaultCombat(pristine), containers: emptyContainers(),
    encounterBrief: defaultBrief(), dmOps: defaultDmOps(), gen: defaultRandomGen()
  };
}
// A fresh Wave slot (spec 2: default name "Wave Encounter N").
export function newWaveEncounter(i, pristine) {
  return {
    meta: Object.assign({}, defaultMeta(i), { name: 'Wave Encounter ' + (i + 1) }),
    combat: defaultCombat(pristine), containers: emptyContainers(),
    encounterBrief: defaultBrief(), dmOps: defaultDmOps(), gen: defaultWaveGen()
  };
}
// Normalize/upgrade a 12-slot generated family array (Random or Wave).
export function normalizeGenArray(src, family, pristine) {
  var arr = Array.isArray(src) ? src : [];
  var isWave = (family === 'wave');
  var prefix = isWave ? 'Wave Encounter ' : 'Random Encounter ';
  var out = [];
  for (var i = 0; i < ENC_COUNT; i++) {
    var e = arr[i] || (isWave ? newWaveEncounter(i, pristine) : newRandomEncounter(i, pristine));
    var dm = Object.assign({}, defaultMeta(i), { name: prefix + (i + 1) });
    e.meta = Object.assign({}, dm, (e.meta || {}));
    if (typeof e.meta.name !== 'string' || !e.meta.name) { e.meta.name = prefix + (i + 1); }
    if (ENC_STATUSES.indexOf(e.meta.status) === -1) { e.meta.status = 'Empty'; }
    if (!Array.isArray(e.meta.tags)) { e.meta.tags = []; }
    if (!THEMES.hasOwnProperty(e.meta.colorTheme)) { e.meta.colorTheme = 'default'; }
    if (typeof e.meta.lastModified !== 'number') { e.meta.lastModified = Date.now(); }
    if (typeof e.meta.saved !== 'boolean') { e.meta.saved = false; }
    if (!e.combat) { e.combat = defaultCombat(pristine); }
    e.containers = Object.assign({}, emptyContainers(), (e.containers || {}));
    e.encounterBrief = normalizeBrief(e.encounterBrief);
    e.dmOps = normalizeDmOps(e.dmOps);
    e.gen = normalizeGen(e.gen, family);
    out.push(e);
  }
  return out;
}

// Normalize / upgrade a loaded store to the current 12-slot shape. Verbatim
// viNormalizeStore (10440-10460): per-slot meta defaulting, name fallback,
// Draft->Design migration, status fallback to Empty, tags guard, colorTheme guard
// against THEMES, lastModified default, container merge, activeIndex clamp.
export function normalizeStore(store, pristine) {
  const s = { version: 3, activeIndex: 0, activeFamily: 'standard', savedMigrated: false, encounters: [] };
  if (!store) { s.savedMigrated = true; }
  else if (store.savedMigrated === true) { s.savedMigrated = true; }
  if (store && typeof store.activeIndex === 'number') { s.activeIndex = store.activeIndex; }
  if (store && FAMILIES.indexOf(store.activeFamily) !== -1) { s.activeFamily = store.activeFamily; }
  const src = (store && Array.isArray(store.encounters)) ? store.encounters : [];
  for (let i = 0; i < ENC_COUNT; i++) {
    const e = src[i] || newEncounterData(i, pristine);
    const dm = defaultMeta(i);
    e.meta = Object.assign({}, dm, (e.meta || {}));
    if (typeof e.meta.name !== 'string' || !e.meta.name) { e.meta.name = 'Encounter ' + (i + 1); }
    if (e.meta.status === 'Draft') { e.meta.status = 'Design'; }
    if (ENC_STATUSES.indexOf(e.meta.status) === -1) { e.meta.status = 'Empty'; }
    if (!Array.isArray(e.meta.tags)) { e.meta.tags = []; }
    if (!THEMES.hasOwnProperty(e.meta.colorTheme)) { e.meta.colorTheme = 'default'; }
    if (typeof e.meta.lastModified !== 'number') { e.meta.lastModified = Date.now(); }
    if (typeof e.meta.saved !== 'boolean') { e.meta.saved = false; }
    if (!e.combat) { e.combat = defaultCombat(pristine); }
    e.containers = Object.assign({}, emptyContainers(), (e.containers || {}));
    e.encounterBrief = normalizeBrief(e.encounterBrief);
    e.dmOps = normalizeDmOps(e.dmOps);
    s.encounters.push(e);
  }
  s.randomEncounters = normalizeGenArray(store ? store.randomEncounters : null, 'random', pristine);
  s.waveEncounters = normalizeGenArray(store ? store.waveEncounters : null, 'wave', pristine);
  if (s.activeIndex < 0 || s.activeIndex >= ENC_COUNT) { s.activeIndex = 0; }
  return s;
}

// The autosave change fingerprint. Verbatim viSig (10516): a stable JSON string
// over the change-relevant subset. This is NOT the save payload - it is only the
// signal the (deferred) autosave runtime diffs. Reads state.players for ps.
export function buildSignature(state) {
  try {
    const s = state || {};
    const monsters = Array.isArray(s.monsters) ? s.monsters : [];
    const players = Array.isArray(s.players) ? s.players : [];
    const ms = monsters.map(function (m) {
      return m ? [m.currentHP, m.maxHP, (m.conditions ? m.conditions.length : 0), (m.customName || m.selectedMonster || ''), (m.notes || ''), (m.treasure ? ((m.treasure.status || '') + ':' + (m.treasure.rev || 0)) : 0)] : 0;
    });
    const ps = players.map(function (p) { return [p.hp, p.maxHp, p.name]; });
    return JSON.stringify([ms, ps, s.currentRound, s.activeMonsterCount, s.currentCombatantIndex, (s.combatLogEntries ? s.combatLogEntries.length : 0), (s.treasureRev || 0), (s.encounterBriefRev || 0), (s.combatRuntimeRev || 0), (s.templatesRev || 0)]);
  } catch (e) {
    return '';
  }
}

// Populated (named) monsters within a stored combat object. Verbatim viPopMonsters
// (10483): a monster counts only when it has a trimmed customName or selectedMonster.
function populatedMonsters(combat) {
  if (!combat || !Array.isArray(combat.monsters)) { return []; }
  return combat.monsters.filter(function (m) {
    return m && ((m.customName && String(m.customName).trim()) || (m.selectedMonster && String(m.selectedMonster).trim()));
  });
}

// Count of populated monsters in a stored combat. Verbatim viMonsterCount (10487).
export function monsterCount(combat) {
  return populatedMonsters(combat).length;
}

// Count of players in a stored combat. Verbatim viPlayerCount (10488), with the
// faithful field mapping: legacy read combat.viPlayers; the React capture stores
// players under combat.players (see captureCombat), so this reads combat.players.
export function playerCount(combat) {
  return (combat && Array.isArray(combat.players)) ? combat.players.length : 0;
}

// Aggregate HP percentage across populated monsters, or null when no max. Verbatim
// viHpPct (10489): clamps each current HP into 0..max, treats NaN current as full.
export function hpPercent(combat) {
  const pm = populatedMonsters(combat);
  let cur = 0;
  let max = 0;
  pm.forEach(function (m) {
    const c = Number(m.currentHP);
    const x = Number(m.maxHP);
    if (!isNaN(x) && x > 0) { max += x; cur += (isNaN(c) ? x : Math.max(0, Math.min(c, x))); }
  });
  if (max <= 0) { return null; }
  return Math.round((cur / max) * 100);
}

// Count of populated monsters that are still alive (currentHP > 0) in a stored
// combat. Verbatim viAliveCount (10495), adapted to take the combat object (the
// legacy read the live `monsters` global directly).
export function aliveCount(combat) {
  try {
    return populatedMonsters(combat).filter(function (m) { return Number(m.currentHP) > 0; }).length;
  } catch (e) {
    return 0;
  }
}

// Number of unlocked encounter slots for a tier. Verbatim viUnlockedSlots (10479):
// basic 4, plus 8, ultimate 12. tier is injected (the medium read that resolves it
// is the deferred persistence runtime's concern).
export function unlockedSlots(tier) {
  return tier === 'basic' ? 4 : (tier === 'plus' ? 8 : 12);
}

// Whether slot i is locked for a tier. Verbatim viSlotLocked (10480).
export function slotLocked(i, tier) {
  return i >= unlockedSlots(tier);
}

// Count SAVED (prepared) encounters per family in a live store (mirror of the
// server-authoritative backend count in combat_commerce.count_prepared_encounters).
// A slot counts only when meta.saved is true AND it holds a populated monster.
// Used for the client quota display + pre-check; the server remains authoritative.
// Whether a slot holds a populated (named/selected) monster.
function slotHasContent(slot) {
  if (!slot) { return false; }
  var combat = slot.combat || {};
  var monsters = Array.isArray(combat.monsters) ? combat.monsters : [];
  for (var i = 0; i < monsters.length; i++) {
    var m = monsters[i];
    if (m && ((m.customName && String(m.customName).trim()) || (m.selectedMonster && String(m.selectedMonster).trim()))) { return true; }
  }
  return false;
}
function slotIsPrepared(slot) {
  return !!(slot && slot.meta && slot.meta.saved) && slotHasContent(slot);
}
// Every populated slot across all families, for the migration selection UI.
export function listPopulatedSlots(store) {
  var out = [];
  if (!store) { return out; }
  var fams = ['standard', 'random', 'wave'];
  var keys = { standard: 'encounters', random: 'randomEncounters', wave: 'waveEncounters' };
  for (var f = 0; f < fams.length; f++) {
    var arr = store[keys[fams[f]]];
    if (!Array.isArray(arr)) { continue; }
    for (var i = 0; i < arr.length; i++) {
      if (slotHasContent(arr[i])) {
        out.push({ family: fams[f], index: i, name: (arr[i].meta && arr[i].meta.name) ? arr[i].meta.name : (fams[f] + ' ' + (i + 1)), saved: !!(arr[i].meta && arr[i].meta.saved) });
      }
    }
  }
  return out;
}
// True when a loaded store has populated data not yet brought under the save authority.
export function needsMigration(store) {
  return !!store && store.savedMigrated !== true && listPopulatedSlots(store).length > 0;
}
export function countSavedEncounters(store) {
  var counts = { standard: 0, random: 0, wave: 0, total: 0 };
  if (!store) { return counts; }
  var keys = { standard: 'encounters', random: 'randomEncounters', wave: 'waveEncounters' };
  var fams = ['standard', 'random', 'wave'];
  for (var f = 0; f < fams.length; f++) {
    var arr = store[keys[fams[f]]];
    if (Array.isArray(arr)) {
      var c = 0;
      for (var i = 0; i < arr.length; i++) { if (slotIsPrepared(arr[i])) { c++; } }
      counts[fams[f]] = c;
      counts.total += c;
    }
  }
  return counts;
}
